import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	MDHeaderComponent,
	MDStepperStepComponent,
	MDStepperStep,
	MDContentBoxComponent,
	MDTagComponent,
	MDButtonToggleComponent,
	MDButtonGroupComponent,
	MD_DIALOG_REF,
	MDDialogModule,
	MDIconComponent
} from '@melodic-ui/components';
import { DividerComponent } from '@lb/components/divider/divider.component';
import { GradeTagComponent } from '../../../grade-tag/grade-tag.component';
import { StepGradeSelectionComponent } from './step-grade-selection.component';
import { ICoreGrade } from '@lb/api/core/interfaces';
import { IGradingTemplateCategoryDetail } from '@lb/api/core/interfaces/igrading-template.interface';
import { resolveNearestGrade } from '@lb/functions/nearest-grade.function';

export interface IProjectGradeResult {
	finalGrade: ICoreGrade | null;
	criteriaGrades: Record<number, number>;
}

@Component({
	selector: 'lb-project-grade-dialog',
	templateUrl: './semester-project-grade.component.html',
	styleUrl: './semester-project-grade.component.scss',
	imports: [
		CommonModule,
		MDHeaderComponent,
		MDStepperStepComponent,
		DividerComponent,
		MDContentBoxComponent,
		GradeTagComponent,
		MDTagComponent,
		MDIconComponent,
		MDDialogModule,
		MDButtonGroupComponent,
		MDButtonToggleComponent
	],
	standalone: true
})
export class SemesterProjectGradeComponent implements OnInit {
	private readonly _dialogRef = inject(MD_DIALOG_REF);

	// ── Inputs ──
	readonly grades = input.required<ICoreGrade[]>();
	readonly projectCategory = input.required<IGradingTemplateCategoryDetail>();
	readonly initialCriteriaGrades = input<Record<number, number | null>>({});

	// ── Local state ──
	readonly criteriaGrades = signal<Record<number, number | null>>({});
	readonly criteriaModifiers = signal<Record<number, '+' | '-' | 'base'>>({});
	readonly currentCriteriaID = signal<number | null>(null);

	// ── Computeds ──
	readonly projectCriteria = computed(() => [...(this.projectCategory().criteria ?? [])].sort((a, b) => a.orderNumber - b.orderNumber));

	readonly gradeOptions = computed(() => this.grades().map((g) => ({ value: g.gradeID, label: g.gradeName })));

	readonly missingGrades = computed(() =>
		this.projectCriteria().filter((c) => {
			const gradeID = this.criteriaGrades()[c.criteriaID];
			return gradeID === null || gradeID === undefined;
		})
	);

	readonly finalGrade = computed<ICoreGrade | null>(() => {
		if (this.missingGrades().length > 0) return null;

		const grades = this.criteriaGrades();
		const modifiers = this.criteriaModifiers();
		let totalWeight = 0;
		let weightedSum = 0;

		for (const criteria of this.projectCriteria()) {
			const effective = this._resolveEffectiveGrade(grades[criteria.criteriaID], modifiers[criteria.criteriaID]);
			if (effective) {
				// Value × whole-percent weight, divided once below: exact for integer values, so a true
				// midpoint reaches the resolver as 10.5 rather than 10.499999999999998, and criteria whose
				// weights do not sum to 100 still average correctly.
				weightedSum += effective.value * criteria.weightPercent;
				totalWeight += criteria.weightPercent;
			}
		}

		if (totalWeight === 0) return null;
		return resolveNearestGrade(weightedSum / totalWeight, this.grades());
	});

	currentCriteria = computed(() => {
		const criteriaID = this.currentCriteriaID();
		return this.projectCriteria().find((c) => c.criteriaID === criteriaID) ?? null;
	});

	currentCriteriaIndex = computed(() => {
		const id = this.currentCriteriaID();
		return this.projectCriteria().findIndex((c) => c.criteriaID === id);
	});

	stepperSteps = computed<MDStepperStep[]>(() => {
		const criteria = this.projectCriteria();
		const modifiers = this.criteriaModifiers();

		return criteria.map((c) => {
			const gradeID = this.criteriaGrades()[c.criteriaID] ?? null;
			const modifier = modifiers[c.criteriaID] ?? null;
			const effective = this._resolveEffectiveGrade(gradeID, modifier);

			return {
				id: c.criteriaID,
				title: c.criteriaName,
				component: StepGradeSelectionComponent,
				inputs: () => ({
					gradeID: effective?.gradeID ?? null,
					grades: this.grades(),
					percentage: c.weightPercent,
					canEdit: true,
					onGradeChange: (gradeID: number | null) => this.onDropdownGradeSelected(c.criteriaID, gradeID)
				})
			} as MDStepperStep;
		});
	});

	canGoNext = computed<boolean>(() => this.currentCriteriaIndex() + 1 < this.projectCriteria().length);
	canGoPrevious = computed<boolean>(() => this.currentCriteriaIndex() - 1 >= 0);

	ngOnInit(): void {
		this._initializeFromExistingGrades();

		const criteria = this.projectCriteria();
		if (criteria.length > 0 && this.currentCriteriaID() === null) {
			this.currentCriteriaID.set(criteria[0].criteriaID);
		}
	}

	goNext(): void {
		if (this.canGoNext()) {
			const nextCriteriaID = this.projectCriteria()[this.currentCriteriaIndex() + 1].criteriaID;
			this.currentCriteriaID.set(nextCriteriaID);
		}
	}

	goPrevious(): void {
		if (this.canGoPrevious()) {
			const previousCriteriaID = this.projectCriteria()[this.currentCriteriaIndex() - 1].criteriaID;
			this.currentCriteriaID.set(previousCriteriaID);
		}
	}

	onDropdownGradeSelected(criteriaID: number, gradeID: number | null): void {
		const selectedGrade = this.grades().find((g) => g.gradeID === gradeID) ?? null;
		const lastChar = selectedGrade ? selectedGrade.gradeName.slice(-1) : '';
		const isModified = lastChar === '+' || lastChar === '-';

		const baseGradeID =
			isModified && selectedGrade ? this.grades().find((g) => g.gradeName === selectedGrade.gradeName.slice(0, -1))?.gradeID ?? null : gradeID;

		this.criteriaGrades.update((g) => ({ ...g, [criteriaID]: baseGradeID }));
		this.criteriaModifiers.update((m) => ({ ...m, [criteriaID]: isModified ? (lastChar as '+' | '-') : 'base' }));
	}

	onRubricChanged(criteriaID: number, gradeID: number | null): void {
		this.criteriaGrades.update((g) => ({ ...g, [criteriaID]: gradeID }));
		this.criteriaModifiers.update((m) => ({ ...m, [criteriaID]: 'base' }));
	}

	onPlusMinusGroupChanged(criteriaID: number, values: unknown[]): void {
		const modifier = (values[0] as '+' | '-' | 'base') ?? 'base';
		this.criteriaModifiers.update((m) => ({ ...m, [criteriaID]: modifier }));
	}

	getModifierForCriteria(criteriaID: number): '+' | '-' | 'base' {
		return this.criteriaModifiers()[criteriaID] ?? 'base';
	}

	onStepClicked(stepId: number): void {
		this.currentCriteriaID.set(stepId);
	}

	close(): void {
		this._dialogRef.close(null);
	}

	save(): void {
		const result = this._buildResult();
		if (result) {
			this._dialogRef.close(result);
		}
	}

	private _buildResult(): IProjectGradeResult | null {
		const final = this.finalGrade();
		if (!final) return null;

		const effectiveCriteriaGrades: Record<number, number> = {};
		const modifiers = this.criteriaModifiers();

		for (const criteria of this.projectCriteria()) {
			const effective = this._resolveEffectiveGrade(this.criteriaGrades()[criteria.criteriaID], modifiers[criteria.criteriaID]);
			if (effective) {
				effectiveCriteriaGrades[criteria.criteriaID] = effective.gradeID;
			}
		}

		return { finalGrade: final, criteriaGrades: effectiveCriteriaGrades };
	}

	private _initializeFromExistingGrades(): void {
		const initial = this.initialCriteriaGrades();
		if (!initial || Object.keys(initial).length === 0) {
			return;
		}

		const nextCriteriaGrades: Record<number, number | null> = {};
		const nextModifiers: Record<number, '+' | '-' | 'base'> = {};

		for (const criteria of this.projectCriteria()) {
			const selectedGradeID = initial[criteria.criteriaID];
			if (selectedGradeID == null) {
				continue;
			}

			const resolved = this._extractBaseGradeAndModifier(selectedGradeID);
			nextCriteriaGrades[criteria.criteriaID] = resolved.baseGradeID;
			nextModifiers[criteria.criteriaID] = resolved.modifier;
		}

		this.criteriaGrades.set(nextCriteriaGrades);
		this.criteriaModifiers.set(nextModifiers);
	}

	private _extractBaseGradeAndModifier(gradeID: number): { baseGradeID: number | null; modifier: '+' | '-' | 'base' } {
		const selectedGrade = this.grades().find((g) => g.gradeID === gradeID) ?? null;
		if (!selectedGrade) {
			return { baseGradeID: gradeID, modifier: 'base' };
		}

		const lastChar = selectedGrade.gradeName.slice(-1);
		const isModified = lastChar === '+' || lastChar === '-';
		if (!isModified) {
			return { baseGradeID: selectedGrade.gradeID, modifier: 'base' };
		}

		const baseName = selectedGrade.gradeName.slice(0, -1);
		const baseGrade = this.grades().find((g) => g.gradeName === baseName) ?? null;
		return {
			baseGradeID: baseGrade?.gradeID ?? selectedGrade.gradeID,
			modifier: lastChar as '+' | '-'
		};
	}

	private _resolveEffectiveGrade(baseGradeID: number | null | undefined, modifier: '+' | '-' | 'base' | undefined): ICoreGrade | null {
		if (baseGradeID === null || baseGradeID === undefined) return null;

		const baseGrade = this.grades().find((g) => g.gradeID === baseGradeID);
		if (!baseGrade) return null;

		if (!modifier || modifier === 'base') return baseGrade;

		const modifiedName = `${baseGrade.gradeName}${modifier}`;
		return this.grades().find((g) => g.gradeName === modifiedName) ?? baseGrade;
	}
}
